import "./App.css";
import "./style.css";

import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { CreatePost } from "./NewPost.js";
import { Link } from "react-router-dom";
import TopNavBar from "../TopNavBar/TopNavBar.js";

import { api, errorMessage } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { CommentForm, CommentList } from "../Comments/Comments";
import InviteNewMemberForm from "../Invite/InviteNewMemberForm";
import PostMedia from "../Post/PostMedia";
import StatusMessage, {
  errorStatus,
  successStatus,
} from "../StatusMessage/StatusMessage";
import { appUrl, shareOrCopy, shareResultMessage } from "../utils/share";

function PostThumbnail({ post, onOpen }) {
  const [thumbIsLoading, setThumbIsLoading] = useState(true);
  const first = post.photo_set[0];
  if (!first) return null;

  return (
    <div className="post-thumbnail">
      <PostMedia
        photo={first}
        className="grid-image"
        preview
        onClick={onOpen}
        onReady={() => setThumbIsLoading(false)}
      />
      {first.is_video && (
        <span className="video-play-badge" aria-hidden="true">
          ▶
        </span>
      )}
      {post.photo_set.length > 1 && (
        <button type="button" className="photo-count-badge" onClick={onOpen}>
          1 / {post.photo_set.length}
        </button>
      )}
      {thumbIsLoading && (
        <div id="loading-overlay" className="grid-image">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
}

function PostImagesModal({ post, onClose }) {
  const [currentImage, setCurrentImage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const photos = post.photo_set;

  const goTo = (index) => {
    setCurrentImage(index);
    setIsLoading(true);
  };

  return (
    <div className="modal active">
      <button className="close-modal" onClick={onClose} aria-label="Close">
        ×
      </button>
      <div className="modal-content">
        <div className="gallery-container">
          {currentImage > 0 && (
            <button
              className="gallery-nav gallery-prev"
              onClick={() => goTo(currentImage - 1)}
              aria-label="Previous"
            >
              &lt;
            </button>
          )}
          <PostMedia
            key={photos[currentImage].photo_guid}
            photo={photos[currentImage]}
            className="gallery-image"
            onReady={() => setIsLoading(false)}
          />
          {currentImage < photos.length - 1 && (
            <button
              className="gallery-nav gallery-next"
              onClick={() => goTo(currentImage + 1)}
              aria-label="Next"
            >
              &gt;
            </button>
          )}
          <div className="gallery-counter">
            {currentImage + 1} / {photos.length}
          </div>
          {isLoading && (
            <div id="loading-overlay" className="loading-overlay">
              <div className="spinner"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// "YYYY-MM-DD" for a date input, in the viewer's timezone
function toDateInputValue(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// A share link lasts until the end of the chosen day, in the viewer's timezone.
function endOfLocalDay(dateInputValue) {
  const [year, month, day] = dateInputValue.split("-").map(Number);
  return new Date(year, month - 1, day + 1);
}

function SharePostForm({ post }) {
  const { session } = useAuth();
  const now = new Date();
  const today = toDateInputValue(now);
  const tomorrow = toDateInputValue(
    new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1),
  );

  // Safari's segmented date input reports "" until every part is filled in
  const [expiryDay, setExpiryDay] = useState(tomorrow);
  const [isCreating, setIsCreating] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [message, setMessage] = useState(null);

  const onShare = async () => {
    if (!expiryDay) {
      setMessage(errorStatus("Choose when the link should expire."));
      return;
    }

    setIsCreating(true);
    setMessage(null);
    let url;
    try {
      const shareCode = await api.post("/family/api/post/share", session.token, {
        post_guid: post.post_guid,
        expiry_date: endOfLocalDay(expiryDay).toISOString(),
      });
      url = appUrl(`/posts/share/${shareCode.guid}`);
      setShareUrl(url);
    } catch (err) {
      setMessage(
        errorStatus(`Couldn't create the share link: ${errorMessage(err)}`),
      );
      return;
    } finally {
      setIsCreating(false);
    }

    const text = shareResultMessage(await shareOrCopy({ url, title: post.title }));
    if (text) setMessage(successStatus(text));
  };

  return (
    <div className="post-sharing-div">
      <div className="share-post-form">
        <div className="share-post-date-input">
          <label>
            Link works until the end of:
            <input
              type="date"
              className="date-input"
              value={expiryDay}
              min={today}
              onChange={(e) => setExpiryDay(e.target.value)}
            />
          </label>
        </div>
        <button onClick={onShare} disabled={isCreating || !expiryDay}>
          {isCreating ? "Creating link..." : "Share Post"}
        </button>
      </div>
      <StatusMessage message={message} onDismiss={() => setMessage(null)} />
      {shareUrl && (
        <input
          className="share-link-input"
          type="text"
          readOnly
          value={shareUrl}
          aria-label="Share link"
          onFocus={(e) => e.target.select()}
        />
      )}
    </div>
  );
}

function Post({ post, onOpenGallery }) {
  const { session } = useAuth();
  const member = session.member;
  const tidyDate = format(Date.parse(post.date), "dd MMM");

  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentList, setCommentList] = useState(post.comment_set);
  const [showShareForm, setShowShareForm] = useState(false);

  return (
    <div className="grid-item" data-post-id={post.post_guid}>
      <PostThumbnail post={post} onOpen={() => onOpenGallery(post)} />
      <div className="item-content">
        <h4>
          {post.member.first_name} {post.member.last_name} - {tidyDate}
        </h4>
        <h3>
          <Link to={`/post/${post.post_guid}`} className="post-title-link">
            {post.title}
          </Link>
        </h3>
        <div dangerouslySetInnerHTML={{ __html: post.content }} />
        <div className="interaction-bar">
          <button
            className="interaction-button"
            onClick={() => setShowCommentForm(!showCommentForm)}
          >
            Comment
          </button>
          {post.member.member_guid === member.member_guid && (
            <div className="post-owner-interaction-options">
              <button
                className="interaction-button"
                onClick={() => setShowShareForm((prev) => !prev)}
              >
                Share {showShareForm && "^"}
              </button>
              <Link to={`/edit/${post.post_guid}`} className="interaction-button">
                Edit Post
              </Link>
            </div>
          )}
        </div>

        {showShareForm && <SharePostForm post={post} />}

        {showCommentForm && (
          <CommentForm
            postGuid={post.post_guid}
            onCommentsChanged={setCommentList}
          />
        )}

        <CommentList comments={commentList} onCommentsChanged={setCommentList} />
      </div>
    </div>
  );
}

/** Append a page of posts, skipping any already shown (new posts shift pages). */
function mergePosts(existing, incoming) {
  const seen = new Set(existing.map((p) => p.post_guid));
  return [...existing, ...incoming.filter((p) => !seen.has(p.post_guid))];
}

export default function Feed() {
  const { session } = useAuth();
  const [posts, setPosts] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [loadMoreMessage, setLoadMoreMessage] = useState(null);
  const [galleryPost, setGalleryPost] = useState(null);
  const [inviteIsActive, setInviteIsActive] = useState(false);

  // paginated: { count, next, previous, results }
  const fetchPage = useCallback(
    (page) => api.get(`/family/api/feed/?page=${page}`, session.token),
    [session.token],
  );

  const loadFirstPage = useCallback(async () => {
    try {
      const page = await fetchPage(1);
      setPosts(page.results);
      setNextPage(page.next ? 2 : null);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, "Couldn't load the feed."));
    } finally {
      setIsLoading(false);
    }
  }, [fetchPage]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = async () => {
    setIsLoadingMore(true);
    setLoadMoreMessage(null);
    try {
      const page = await fetchPage(nextPage);
      setPosts((prev) => mergePosts(prev, page.results));
      setNextPage(page.next ? nextPage + 1 : null);
    } catch (err) {
      setLoadMoreMessage(
        errorStatus(`Couldn't load more posts: ${errorMessage(err)}`),
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (isLoading) return <div> Loading ... </div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <TopNavBar
        inviteIsActive={inviteIsActive}
        setInviteIsActive={setInviteIsActive}
      />
      <CreatePost onCreated={loadFirstPage} />

      <div className="masonry-grid">
        {inviteIsActive && <InviteNewMemberForm />}
        {posts.length > 0 ? (
          posts.map((post) => (
            <Post key={post.post_guid} post={post} onOpenGallery={setGalleryPost} />
          ))
        ) : (
          <div>No posts available</div>
        )}
      </div>

      <div className="load-more">
        <StatusMessage
          message={loadMoreMessage}
          onDismiss={() => setLoadMoreMessage(null)}
        />
        {nextPage && (
          <button
            type="button"
            className="interaction-button"
            onClick={loadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? "Loading..." : "Load more posts"}
          </button>
        )}
      </div>

      {galleryPost && (
        <PostImagesModal
          key={galleryPost.post_guid}
          post={galleryPost}
          onClose={() => setGalleryPost(null)}
        />
      )}
    </>
  );
}
